/**
 * Every popup behaves the same way:
 * - drag it by its title bar (mouse or finger) to move it; it remembers where you left it, each
 *   popup on its own, and a double tap or double click on the title bar puts it back;
 * - tap or click anywhere outside it to close it (except where closing by accident would hurt:
 *   a trade, the middle of hand-forging).
 */
const KEY = 'hollowmere-pos:';
const DRAG_IGNORE = 'button, input, select, textarea, a, [role="button"]';

interface Popup { el: HTMLElement; key: string; outside: boolean; close?: () => void; canClose?: () => boolean; openers: HTMLElement[] }
const popups: Popup[] = [];
let closedAt = 0;
/** Did a tap outside just close a popup? (So the same tap doesn't also walk you somewhere.) */
export const justClosedPopup = () => performance.now() - closedAt < 450;

function savedPos(key: string): { x: number; y: number } | null {
  try { const v = localStorage.getItem(KEY + key); return v ? JSON.parse(v) : null; } catch { return null; }
}
function savePos(key: string, x: number, y: number) { try { localStorage.setItem(KEY + key, JSON.stringify({ x, y })); } catch { /* private window */ } }
function forget(key: string) { try { localStorage.removeItem(KEY + key); } catch { /* ignore */ } }

/** Pin a popup at (x, y) px from the top left, kept on screen. */
function placeAt(el: HTMLElement, x: number, y: number) {
  const r = el.getBoundingClientRect(), W = window.innerWidth, H = window.innerHeight;
  const cx = Math.max(8 - r.width + 60, Math.min(W - 60, x)), cy = Math.max(4, Math.min(H - 44, y));
  Object.assign(el.style, { left: `${cx}px`, top: `${cy}px`, right: 'auto', bottom: 'auto', transform: 'none', margin: '0' });
  return [cx, cy];
}
function unpin(el: HTMLElement) { for (const k of ['left', 'top', 'right', 'bottom', 'transform', 'margin'] as const) el.style[k] = ''; }

/**
 * Make `el` a movable popup. `handle` is its title bar. `close` is how it closes (when you tap
 * outside it, if `outside` is on); `openers` are the buttons that open it (tapping those doesn't
 * count as outside).
 */
export function popup(el: HTMLElement, handle: HTMLElement, key: string, opts: { close?: () => void; outside?: boolean; canClose?: () => boolean; openers?: HTMLElement[] } = {}) {
  const p: Popup = { el, key, outside: opts.outside ?? true, close: opts.close, canClose: opts.canClose, openers: opts.openers ?? [] };
  popups.push(p);
  handle.classList.add('drag-handle');
  // put it back where it was left each time it opens
  const restore = () => { if (el.hidden) return; const s = savedPos(key); if (s) requestAnimationFrame(() => placeAt(el, s.x * window.innerWidth, s.y * window.innerHeight)); };
  new MutationObserver(restore).observe(el, { attributes: true, attributeFilter: ['hidden'] });
  restore();

  let drag: { id: number; dx: number; dy: number; moved: boolean; sx: number; sy: number } | null = null, lastTap = 0;
  handle.addEventListener('pointerdown', e => {
    if ((e.target as HTMLElement).closest(DRAG_IGNORE)) return;
    const r = el.getBoundingClientRect();
    drag = { id: e.pointerId, dx: e.clientX - r.left, dy: e.clientY - r.top, moved: false, sx: e.clientX, sy: e.clientY };
    handle.setPointerCapture(e.pointerId);
    e.preventDefault();
  });
  handle.addEventListener('pointermove', e => {
    if (!drag || e.pointerId !== drag.id) return;
    if (!drag.moved && Math.hypot(e.clientX - drag.sx, e.clientY - drag.sy) < 4) return;
    drag.moved = true;
    placeAt(el, e.clientX - drag.dx, e.clientY - drag.dy);
  });
  const end = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    if (drag.moved) { const r = el.getBoundingClientRect(); savePos(key, r.left / window.innerWidth, r.top / window.innerHeight); }
    else { const now = performance.now(); if (now - lastTap < 350) { forget(key); unpin(el); } lastTap = now; }
    drag = null;
  };
  handle.addEventListener('pointerup', end);
  handle.addEventListener('pointercancel', end);
  handle.addEventListener('dblclick', () => { forget(key); unpin(el); });
}

// one listener for all of them: a press outside every open popup closes the top one that allows it
document.addEventListener('pointerdown', e => {
  const t = e.target as Node;
  const open = popups.filter(p => !p.el.hidden && p.el.isConnected);
  if (!open.length || open.some(p => p.el.contains(t) || p.openers.some(o => o.contains(t)))) return;
  // anything else on screen that is itself a popup-ish control (the HUD, the minimap) still counts as outside
  let closed = false;
  for (const p of open) if (p.outside && p.close && (p.canClose?.() ?? true)) { p.close(); if (p.el.hidden) closed = true; }
  // only when a popup really went away does this tap count as "closing it" (and not walk you somewhere)
  if (closed) closedAt = performance.now();
}, true);
